import { useMemo, useState, useEffect } from 'react';
import { addGlobalRegion } from '@intelligence/v3/utils/region';
import { fetchRegionOptionsMapByStoreTops } from '@intelligence/v3/services/topCharts';
import { handleRegionsCountriesData2StandardNew } from '@intelligence/utils/regionSelectUtils';
import { nintendoUSRegionsOptions } from '@intelligence/_config/storeRank/console';
import { getStoreTopsMapByPlatforms } from '@intelligence/v3/_config/platformStoreTops';

import type { IRegion, IGameType } from '@intelligence/types/common';

/**
 * 商店榜单地区选项配置接口
 * @author lv.chen
 */
interface IStoreTopRegionsConfig {
  /** 是否需要执行，即调接口获取国家列表数据 */
  executable?: boolean;
  /** 实体类型 */
  entityType: IGameType;
  /** 平台配置 */
  platforms: IPlatformConfig;
}

/**
 * Hook返回值接口
 * @author lv.chen
 */
interface IUseStoreTopRegionsOptionsMapReturn {
  /** 加载状态 */
  isLoading: boolean;
  /** 当前平台的地区选项 */
  regionsOptions: IRegion[];
  /** 所有平台的地区选项映射 */
  regionsOptionsMap: IRecord<IRegion[]>;
  /** 错误信息 */
  error: IErrorInfo | null;
  /** 重试函数 */
  retry: () => void;
}

/**
 * 获取商店榜单地区选项映射的Hook
 * @author lv.chen
 * @param platform 当前平台
 * @param cfg 配置参数
 * @param isNintendoNoGlobal 是否为Nintendo无全球选项模式
 * @returns 包含加载状态、地区选项、地区选项映射、错误信息和重试函数的对象
 */
export default function useStoreTopRegionsOptionsMap(
  platform: string,
  cfg: IStoreTopRegionsConfig,
  isNintendoNoGlobal = true,
): IUseStoreTopRegionsOptionsMapReturn {
  const { executable = true, platforms } = cfg ?? {};

  const [isLoading, setIsLoading] = useState(true);
  const [regionsOptionsMap, setRegionsOptionsMap] = useState<IRecord<IRegion[]>>({});
  const [error, setError] = useState<IErrorInfo | null>(null);

  // 缓存商店榜单映射计算
  const storeTopsMap = useMemo<IRecord<string[]>>(() => {
    return getStoreTopsMapByPlatforms(platforms);
  }, [platforms]);

  /**
   * 处理Nintendo平台的特殊逻辑
   * @author lv.chen
   * @param regionsOptionsMap 地区选项映射
   * @param isNintendoNoGlobal 是否为Nintendo无全球选项模式
   * @returns 处理后的地区选项映射
   */
  const processNintendoRegions = (
    regionsOptionsMap: IRecord<IRegion[]>,
    isNintendoNoGlobal: boolean,
  ): IRecord<IRegion[]> => {
    const result = { ...regionsOptionsMap };

    // 处理Nintendo平台的特殊逻辑
    const nintendoRegionOptions = result?.nintendo || [];
    // @ts-ignore
    result.nintendo = isNintendoNoGlobal
      ? nintendoRegionOptions.filter((region) => region?.area_code?.toLowerCase() !== 'global')
      : addGlobalRegion(nintendoRegionOptions);

    // 添加Nintendo美国专用选项
    result.nintendoUsOnly = isNintendoNoGlobal
      ? nintendoUSRegionsOptions.filter((region) => region?.area_code?.toLowerCase() !== 'global')
      : nintendoUSRegionsOptions;

    return result;
  };

  // 缓存当前平台的地区选项
  const regionsOptions = useMemo<IRegion[]>(() => {
    if (isLoading) return [];
    return regionsOptionsMap?.[platform] ?? [];
  }, [isLoading, platform, regionsOptionsMap]);

  /**
   * 更新地区选项映射数据
   * @author lv.chen
   * @description 从服务端获取地区选项数据并处理
   */
  const updateRegionsOptionsMap = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      // 调用API获取地区选项数据
      const response = await fetchRegionOptionsMapByStoreTops({ target: storeTopsMap });
      const platformRegionOptions = response?.data ?? {};

      // 检查数据有效性
      if (!platformRegionOptions) {
        setRegionsOptionsMap({});
        return;
      }

      // 转换原始数据为标准格式
      const regionsOptionsMapTmp: IRecord<IRegion[]> = {};
      Object.entries(platformRegionOptions).forEach(([platformKey, regionOptions]) => {
        if (platformKey) {
          regionsOptionsMapTmp[platformKey] = handleRegionsCountriesData2StandardNew(regionOptions as any);
        }
      });

      // 应用Nintendo平台特殊处理逻辑
      const finalRegionsOptionsMap = processNintendoRegions(regionsOptionsMapTmp, isNintendoNoGlobal);
      setRegionsOptionsMap(finalRegionsOptionsMap);
    } catch (err: any) {
      // 构建详细的错误信息
      const errorInfo: IErrorInfo = {
        code: err?.code || err?.status,
        message: err?.message || '获取地区选项数据失败',
        details: err,
      };
      setError(errorInfo);
      setRegionsOptionsMap({});
      console.error('获取商店榜单地区选项失败:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 重试获取数据的函数
   * @author lv.chen
   * @description 手动触发重新获取地区选项数据
   */
  const retry = (): void => {
    if (executable) {
      updateRegionsOptionsMap();
    }
  };

  // 监听依赖变化，自动重新获取数据
  useEffect(() => {
    if (executable) {
      updateRegionsOptionsMap();
    }
  }, [executable, storeTopsMap, isNintendoNoGlobal]);

  return {
    isLoading,
    regionsOptions,
    regionsOptionsMap,
    error,
    retry,
  };
}
